import type { ComponentProps } from 'react';
import type { Ionicons } from '@expo/vector-icons';
import type { BusinessCapability } from '../../shell/session/domain/businessProfile';
import { OPERATION_MODE_TOGGLES } from './operationModeToggles';

/** Capability codes shown in Funciones del negocio. */
export const BUSINESS_FUNCTION_CODES = [
  'appointments',
  'barcode_scan',
  'cash_sessions',
  'catalog',
  'catalog_virtual',
  'checks',
  'commissions',
  'customer_credit_fiado',
  'customer_loyalty',
  'delivery_fulfillment',
  'employees',
  'expenses',
  'external_services',
  'fiscal_electronic',
  'inventory',
  'kitchen_tickets',
  'pickup_display',
  'menu_modifiers',
  'misc_quick_sale',
  'mp_oauth',
  'mp_qr',
  'on_duty',
  'pos',
  'price_lists',
  'product_combos',
  'product_pharmacy_fields',
  'product_variants',
  'promotions',
  'purchases',
  'recipes',
  'reports',
  'sales_history',
  'service_catalog',
  'service_packages',
  'service_quotes',
  'shelf_labels',
  'split_bill',
  'pos_tips',
  'tables',
  'weight_uom',
  'work_orders',
] as const;

export type BusinessFunctionCode = (typeof BUSINESS_FUNCTION_CODES)[number];

export const NON_TOGGLEABLE_FUNCTIONS: ReadonlySet<BusinessFunctionCode> = new Set(['pos', 'employees']);

export const HIDDEN_FUNCTIONS: ReadonlySet<string> = new Set();

export type BusinessFunctionGroupId =
  | 'sales'
  | 'stock'
  | 'customers'
  | 'finance'
  | 'operations'
  | 'services'
  | 'hospitality'
  | 'online';

export type BusinessFunctionDef = {
  code: BusinessFunctionCode | 'storefront';
  label: string;
  explanation: string;
  hideConfirm: string;
  iconName: ComponentProps<typeof Ionicons>['name'];
  group: BusinessFunctionGroupId;
  capability: BusinessCapability | null;
  planLock?: 'fiado' | 'promotions';
  /** Uses operation_mode instead of override RPC. */
  usesOperationMode?: boolean;
};

const EXTRA_LABELS: Partial<Record<BusinessFunctionCode, string>> = {
  catalog: 'Catálogo',
  catalog_virtual: 'Catálogo virtual',
  sales_history: 'Historial de ventas',
  reports: 'Reportes',
  employees: 'Equipo',
  pos: 'Punto de venta',
  fiscal_electronic: 'Factura electrónica',
  mp_qr: 'QR Mercado Pago',
  mp_oauth: 'OAuth Mercado Pago',
  shelf_labels: 'Etiquetas de estante',
  recipes: 'Recetas / producción',
  checks: 'Cheques',
  product_combos: 'Combos',
  product_pharmacy_fields: 'Campos de farmacia',
};

const EXTRA_EXPLANATIONS: Partial<Record<BusinessFunctionCode, string>> = {
  catalog: 'Productos y precios en el POS.',
  catalog_virtual: 'Catálogo para venta online sin stock físico.',
  sales_history: 'Listado de ventas anteriores.',
  reports: 'Reportes y métricas del negocio.',
  employees: 'Usuarios y permisos del equipo.',
  pos: 'Cobro en mostrador. Siempre activo.',
  fiscal_electronic: 'Facturación electrónica ARCA.',
  mp_qr: 'Cobro con QR de Mercado Pago.',
  mp_oauth: 'Vincular cuenta de Mercado Pago.',
  checks: 'Aceptar cheques como medio de cobro en ventas y cobros de cuenta.',
  shelf_labels: 'Imprimir etiquetas de estante.',
  recipes: 'Definí la receta. Baja insumos y suma el terminado. También sirve para fraccionar.',
  product_combos: 'Un producto que descuenta el stock de varios componentes y llega a cocina desarmado.',
  product_pharmacy_fields: 'Principio activo y "para qué se usa" en la ficha del producto.',
};

const EXTRA_ICONS: Partial<Record<BusinessFunctionCode, ComponentProps<typeof Ionicons>['name']>> = {
  product_combos: 'layers-outline',
  product_pharmacy_fields: 'medkit-outline',
};

const GROUP_LABELS: Record<BusinessFunctionGroupId, string> = {
  sales: 'Ventas',
  stock: 'Stock y compras',
  customers: 'Clientes',
  finance: 'Finanzas',
  operations: 'Operación',
  services: 'Servicios',
  hospitality: 'Salón y cocina',
  online: 'Online',
};

const GROUP_ORDER: BusinessFunctionGroupId[] = [
  'sales',
  'stock',
  'customers',
  'finance',
  'operations',
  'services',
  'hospitality',
  'online',
];

const CAPABILITY_GROUP: Partial<Record<BusinessFunctionCode, BusinessFunctionGroupId>> = {
  catalog: 'sales',
  catalog_virtual: 'online',
  sales_history: 'sales',
  misc_quick_sale: 'sales',
  split_bill: 'sales',
  barcode_scan: 'sales',
  weight_uom: 'sales',
  product_variants: 'sales',
  product_combos: 'sales',
  product_pharmacy_fields: 'sales',
  inventory: 'stock',
  purchases: 'stock',
  recipes: 'stock',
  shelf_labels: 'stock',
  customer_credit_fiado: 'customers',
  customer_loyalty: 'customers',
  promotions: 'customers',
  price_lists: 'customers',
  cash_sessions: 'finance',
  checks: 'finance',
  expenses: 'finance',
  fiscal_electronic: 'finance',
  mp_qr: 'finance',
  mp_oauth: 'finance',
  commissions: 'finance',
  pos: 'operations',
  employees: 'operations',
  reports: 'operations',
  service_catalog: 'services',
  service_packages: 'services',
  service_quotes: 'services',
  appointments: 'services',
  work_orders: 'services',
  external_services: 'services',
  kitchen_tickets: 'hospitality',
  pickup_display: 'hospitality',
  tables: 'hospitality',
  pos_tips: 'hospitality',
  menu_modifiers: 'hospitality',
  delivery_fulfillment: 'online',
  on_duty: 'online',
};

function toggleMeta(code: BusinessFunctionCode): Pick<BusinessFunctionDef, 'label' | 'explanation' | 'hideConfirm' | 'iconName' | 'planLock'> {
  const toggle = OPERATION_MODE_TOGGLES.find((t) => t.capability === code);
  if (toggle) {
    return {
      label: toggle.label.replace(/^¿|(\?)$/g, '').replace(/\?$/, ''),
      explanation: toggle.explanation,
      hideConfirm: toggle.hideConfirm,
      iconName: toggle.iconName,
      planLock: toggle.planLock,
    };
  }
  return {
    label: EXTRA_LABELS[code] ?? code,
    explanation: EXTRA_EXPLANATIONS[code] ?? '',
    hideConfirm: 'Se oculta del menú. Los datos quedan.',
    iconName: EXTRA_ICONS[code] ?? 'ellipse-outline',
  };
}

function buildCapabilityDef(code: BusinessFunctionCode): BusinessFunctionDef {
  const meta = toggleMeta(code);
  return {
    code,
    group: CAPABILITY_GROUP[code] ?? 'operations',
    capability: code,
    ...meta,
  };
}

export const BUSINESS_FUNCTION_DEFINITIONS: BusinessFunctionDef[] = [
  ...BUSINESS_FUNCTION_CODES.filter((c) => !HIDDEN_FUNCTIONS.has(c)).map(buildCapabilityDef),
  {
    code: 'storefront',
    label: 'Tienda online',
    explanation: 'Pedidos por internet. Si lo apagás, se ocultan Pedidos y la tienda. El perfil queda.',
    hideConfirm: 'Se ocultan Pedidos y la tienda. El perfil queda.',
    iconName: 'storefront-outline',
    group: 'online',
    capability: null,
    usesOperationMode: true,
  },
];

export const BUSINESS_FUNCTIONS_HUB_ROW = {
  title: 'Funciones del negocio',
  description: 'Prendé o apagá lo que usás. Tu rubro es solo el punto de partida.',
} as const;

export function groupBusinessFunctions<T extends BusinessFunctionDef>(
  rows: T[],
): { id: BusinessFunctionGroupId; label: string; rows: T[] }[] {
  const byGroup = new Map<BusinessFunctionGroupId, T[]>();
  for (const row of rows) {
    const list = byGroup.get(row.group) ?? [];
    list.push(row);
    byGroup.set(row.group, list);
  }
  return GROUP_ORDER.filter((id) => byGroup.has(id)).map((id) => ({
    id,
    label: GROUP_LABELS[id],
    rows: byGroup.get(id) ?? [],
  }));
}

export function isBusinessFunctionToggleable(code: string): boolean {
  if (HIDDEN_FUNCTIONS.has(code)) return false;
  return !NON_TOGGLEABLE_FUNCTIONS.has(code as BusinessFunctionCode);
}
